import { useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { usePulseSegments } from '@pulse/react';

import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';

import JSONLinkedData, { SchemaType } from 'components/JSONLinkedData';
import MasonryGrid, { MasonryGridSkeleton } from 'components/TemplatesMasonryGrid';

import useRelatedCategoriesDataStore from 'templates/stores/relatedCategoriesDataStore';
import useMasonryDataStore from 'templates/stores/masonryDataStore';
import { getPaginationInfo } from 'templates/utils';
import FirstElement from 'components/TemplatesMasonryGrid/FirstElement';

import NoResult from '../NoResult';
import Pagination from '../Pagination';
import RelatedCategories from '../RelatedCategories';

import useStyles from './styles';

const CategoryGrid = () => {
  const classes = useStyles();

  const { category, subCategory } = useParams();
  const [searchParams] = useSearchParams();

  const isSearchPage = category === 'search';

  const { masonryData, fetchMasonryData, searchTemplates, isLoading } = useMasonryDataStore(
    state => ({
      fetchMasonryData: state.fetchMasonryData,
      searchTemplates: state.searchTemplates,
      masonryData: state.masonryData,
      isLoading: state.isLoading,
    }),
  );

  const { relatedCategories, fetchRelatedCategories } = useRelatedCategoriesDataStore(state => ({
    relatedCategories: state.relatedCategories,
    fetchRelatedCategories: state.fetchRelatedCategories,
  }));

  const { segments, loading: isSegmentsLoading } = usePulseSegments();

  useEffect(() => {
    if (isSegmentsLoading) return;

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      try {
        if (isSearchPage) {
          await searchTemplates(searchParams, segments);
          return;
        }
        await fetchMasonryData(category, subCategory, searchParams, segments);
        await fetchRelatedCategories(category, subCategory, segments);
      } catch (err) {
        window.location.href = '/not-found';
      }
    })();
  }, [
    category,
    fetchMasonryData,
    fetchRelatedCategories,
    searchParams,
    searchTemplates,
    subCategory,
    isSegmentsLoading,
    segments,
    isSearchPage,
  ]);

  const { pagesCount } = useMemo(() => getPaginationInfo({ masonryData }), [masonryData]);

  const isShownRelatedCategories = Boolean(relatedCategories?.[0]?.targets?.length);

  const MemoizedFirstElement = useMemo(() => <FirstElement categoryName={category} />, [category]);

  if (isLoading) {
    return <MasonryGridSkeleton />;
  }

  return (
    <HorizontalContainer
      className={classes.masonryGridWrapper}
      dataTestId="templates-category-grid"
    >
      {masonryData.data.length ? (
        <>
          <MasonryGrid masonryData={masonryData.data} FirstElement={MemoizedFirstElement} />
          <div className={classes.paginationHolder} data-pulse-section="masonry-grid-pagination">
            <Pagination pagesCount={pagesCount} />
          </div>

          {isShownRelatedCategories && <RelatedCategories related={relatedCategories[0].targets} />}
          <JSONLinkedData data={masonryData} type={SchemaType.WebPage} />
        </>
      ) : (
        <NoResult />
      )}
    </HorizontalContainer>
  );
};

export default CategoryGrid;
